import {
  Directive,
  ElementRef,
  Injectable,
  Input,
  NgZone,
  OnDestroy,
} from "@angular/core";

import { formatShort, shortUnitFor } from "./short-count";

// ===========================================================================
// Qlik-style KPI number transitions for the static dashboards.
//
// A figure that jumps straight to its new value says nothing about which way it
// moved or by how much. Qlik counts it across instead, and the count IS the
// feedback that the click landed — which is the whole point on these screens,
// where a KPI card is both a number and a filter source.
//
// Lives at dashboard/static/ rather than inside either screen because DPM and
// DEM both use it (see static-dashboards.ts: anything genuinely shared between
// static dashboards belongs at this level). It is deliberately NOT in
// components/shared/ — nothing dynamic uses it yet, and moving it there is the
// change to make on the day one does.
//
// Two pieces, and the split matters:
//
//   NumberTweenTicker   one requestAnimationFrame loop for every figure on the
//                       page, so a filter click that moves ten numbers is ten
//                       writes per frame rather than ten frame loops. It also
//                       means they are driven off the same timestamp, so they
//                       start, run and land together instead of drifting apart
//                       by a frame each.
//
//   AnimatedNumberDirective  one per figure. Owns its own from/to and paints
//                       straight into textContent.
//
// The whole thing runs OUTSIDE Angular's zone and writes no bindings, so a
// 600ms transition across a KPI row costs zero change-detection passes. That is
// not an optimisation for its own sake: these screens bind template METHODS for
// the cross-filter states (isDimmed, isPicked, xfTitle, and the same again on
// every heat tile and grid row), so a change-detection pass here is expensive
// enough that running 60 of them a second would visibly stutter the very
// animation they were meant to drive.
// ===========================================================================

/** Something the ticker drives. `tick` returns false when it is finished. */
interface Tweenable {
  tick(now: number): boolean;
}

/**
 * The single animation frame loop every animated figure shares.
 *
 * Root-provided rather than per-component: the loop is a browser-wide resource,
 * and one of them is the point. It idles at zero cost — the rAF is only
 * scheduled while something is actually moving, and cancelled the moment the
 * last tween finishes, so nothing keeps ticking behind a screen at rest.
 */
@Injectable({ providedIn: "root" })
export class NumberTweenTicker {
  private readonly active = new Set<Tweenable>();
  private raf = 0;

  constructor(private readonly zone: NgZone) {}

  join(t: Tweenable): void {
    this.active.add(t);
    if (!this.raf) {
      // Outside the zone: a tween writes textContent itself and leaves nothing
      // for change detection to find, so the frames must not schedule any.
      this.zone.runOutsideAngular(() => {
        this.raf = requestAnimationFrame(this.frame);
      });
    }
  }

  /** Drop a tween — because it finished, or because its element is being
   *  destroyed. Both go through here so the loop can never outlive its work. */
  leave(t: Tweenable): void {
    if (!this.active.delete(t)) return;
    if (!this.active.size && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  private readonly frame = (now: number): void => {
    // Deleting the current entry mid-forEach is well-defined for a Set, and a
    // tween that retargets itself is already in the set, so neither case needs
    // a copy.
    this.active.forEach((t) => {
      if (!t.tick(now)) this.active.delete(t);
    });
    this.raf = this.active.size ? requestAnimationFrame(this.frame) : 0;
  };
}

/** Qlik's feel: fast off the mark, settling into the new figure. */
function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/**
 * How many decimals to print, taken from the value being counted TO.
 *
 * Inferred rather than configured because every figure these screens animate is
 * a record count: an integer target prints integers, so a count of 7 can never
 * flicker through "6.4" on its way. A fractional target (a percentage, if one is
 * ever wired up) keeps its own precision instead of being rounded away.
 */
function decimalsOf(v: number): number {
  if (!Number.isFinite(v) || Number.isInteger(v)) return 0;
  const s = String(v);
  const dot = s.indexOf(".");
  return dot < 0 ? 0 : Math.min(2, s.length - dot - 1);
}

/**
 * Count an element's number across to its new value.
 *
 *   <div class="kpi-value" [duNumberShort]="true" [duNumber]="k.value"></div>
 *
 * The element's text is the directive's to write, so the template carries NO
 * interpolation — `{{ k.value }}` alongside this would have Angular stamp the
 * final figure in on the same pass that starts the count, which is one frame of
 * the answer before the animation of it.
 *
 * Rules it keeps, all four of which are about not lying to the eye:
 *
 *  - The FIRST value is painted, not counted. These figures live behind
 *    `*ngIf="!loading"`, so their first render already holds the answer the load
 *    returned; sweeping it up from zero would animate a change that never
 *    happened. Every value after that counts.
 *  - A value that has not changed does nothing at all — no restart, no reflow.
 *    Change detection re-runs these bindings constantly, and a filter click that
 *    leaves Missing on 4 must leave the 4 alone.
 *  - A value that changes MID-COUNT retargets from what is on screen right now
 *    rather than from where the last count began, so rapid clicking stays
 *    continuous and can never flash a stale figure.
 *  - The last frame writes the target exactly, so a count always ends on the
 *    real number and not on a rounded approach to it.
 *
 * `duNumberShort` abbreviates what it prints ("12.45K") without changing any of
 * the above: the count still runs on the real value and still lands on it, and
 * only the painting of it is shortened. See short-count.ts.
 */
@Directive({
    selector: "[duNumber]",
    standalone: false
})
export class AnimatedNumberDirective implements OnDestroy, Tweenable {
  /** The figure to show. */
  @Input("duNumber")
  set value(v: number | string | null | undefined) {
    const next = Number(v);
    const target = Number.isFinite(next) ? next : 0;

    if (!this.started) {
      // First paint: the value arrived with the element, so it is not a change.
      this.started = true;
      this.target = target;
      this.current = target;
      this.decimals = decimalsOf(target);
      this.paint(target);
      return;
    }

    if (target === this.target) return; // nothing moved — leave it be

    this.target = target;
    this.decimals = decimalsOf(target);

    if (this.duration <= 0 || this.reducedMotion()) {
      this.ticker.leave(this);
      this.current = target;
      this.paint(target);
      return;
    }

    this.from = this.current; // whatever the eye is on, not where the last run began
    this.startedAt = -1; // stamped by the first frame, so no clock read here
    this.ticker.join(this);
  }

  /** Milliseconds the count takes. 0 (or reduced motion) snaps. */
  @Input("duNumberDuration") duration = 600;

  /**
   * Group thousands (12,450 rather than 12450).
   *
   * Off, so this directive prints exactly what the interpolation it replaced
   * printed and no card's layout moves. Turn it on per figure the day a screen
   * wants grouped counts.
   *
   * Ignored while `duNumberShort` is on — shorthand groups on its own, because
   * the pipe it mirrors does.
   */
  @Input("duNumberGroup") group = false;

  /**
   * Abbreviate the figure — 12,450 as "12.45K" — exactly as the real KPI cards
   * do (see short-count.ts, and card2kpi's `numberFormat` pipe behind it).
   *
   * For the KPI cards only. The donut's figures stay exact; it keeps its lines
   * inside the card by scaling its type instead.
   *
   * A setter rather than a field so the flag can arrive in any order relative to
   * [duNumber]: a plain field set after the first value would leave the figure
   * painted long until something else moved it. Coerced the way the CDK coerces
   * a boolean input, so a bare `duNumberShort` attribute reads as true rather
   * than as the empty string it literally is.
   */
  @Input("duNumberShort")
  set short(v: boolean | string | null | undefined) {
    const on = v !== null && v !== undefined && `${v}` !== "false";
    if (on === this.abbreviate) return;
    this.abbreviate = on;
    // Only if there is already a figure on screen — before the first value there
    // is nothing to repaint, and painting here would put a 0 in the element the
    // "first value is painted, not counted" rule is written to avoid.
    if (this.started) this.paint(this.current);
  }

  private abbreviate = false;
  private started = false;
  private target = 0;
  private current = 0;
  private from = 0;
  private startedAt = -1;
  private decimals = 0;
  private readonly motionQuery =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly ticker: NumberTweenTicker
  ) {}

  tick(now: number): boolean {
    if (this.startedAt < 0) this.startedAt = now;
    const t = Math.min(1, (now - this.startedAt) / this.duration);
    if (t >= 1) {
      this.current = this.target;
      this.paint(this.target);
      return false;
    }
    this.current = this.from + (this.target - this.from) * easeOutCubic(t);
    this.paint(this.current);
    return true;
  }

  ngOnDestroy(): void {
    // A card torn down mid-count (a brand change puts the row back into its
    // loading state) must not leave the loop holding a detached element.
    this.ticker.leave(this);
  }

  /** Read per change rather than cached, so switching the OS setting takes
   *  effect on the next click instead of on the next reload. */
  private reducedMotion(): boolean {
    return !!this.motionQuery?.matches;
  }

  private paint(n: number): void {
    // Shorthand takes its unit from the TARGET, not from `n`, so a running count
    // keeps one unit and one width for its whole run instead of flipping from
    // "9,999" to "10.00K" a frame before it lands — see shortUnitFor(). Derived
    // here rather than cached on the target setter because it is three
    // comparisons, and a value that cannot go stale is worth more than they cost.
    if (this.abbreviate) {
      this.el.nativeElement.textContent = formatShort(
        n,
        shortUnitFor(this.target)
      );
      return;
    }
    const d = this.decimals;
    this.el.nativeElement.textContent = this.group
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : n.toFixed(d);
  }
}
